/**
 * Реестр карточек разбора метрик.
 *
 * ═══ ТЕКСТ ПЕРЕЕХАЛ В СЛОВАРИ — 2026-08-07 ═══
 *
 * Здесь жили заголовки, объяснения и пороги на ДВУХ языках: `ru` и `en`.
 * Ящик разбора умел ровно их — `i18n.language.startsWith("ru") ? ru : en`,
 * — то есть немец и украинец получали английский текст в приложении, где
 * всё остальное переведено. К запуску заявлены четыре языка.
 *
 * Второе, что чинит переезд: копия лежала МИМО словарей, и ни один
 * i18n-сторож в неё не смотрел. Строка «нет потолка roадствует» —
 * след массовой замены — прожила здесь до 2026-08-06 именно поэтому.
 *
 * Теперь текст живёт в `i18n/locales/<loc>/drill.json` под ключами
 * `<метрика>.title` · `.interpretation` · `.when_concerning` (массив), а
 * в коде остаётся то, что НЕ переводится: ссылка на первоисточник и
 * формула.
 *
 * ФОРМУЛА ЗДЕСЬ СПРАВОЧНАЯ. Пользователю она не показывается — канон
 * просит объяснять смысл, а не показывать запись расчёта. Держится в
 * коде как ориентир для того, кто читает карточку глазами разработчика,
 * и поэтому не переводится.
 *
 * Доказательная база (осталась прежней):
 *   Banister 1975, Coggan 2003 — CTL/ATL/TSB · Foster 1998, 2001 —
 *   session-RPE, монотонность · Soligard 2016 + мета 2025 — ACWR ·
 *   Plews 2017, Altini 2024 — ВСР · Mujika 2014 — подводка ·
 *   Seiler 2010, Stöggl 2014 — поляризация · Minetti 2002 — стоимость
 *   уклона · обзор биомеханики и экономичности бега 2024 — техника.
 */

export interface DrillMeta {
  /** Ключ метрики. Совпадает с MetricResult.name бэкенда и с префиксом
      ключей в словаре `drill`. */
  key: string;
  /** Один канонический адрес — рецензируемая работа, где возможно. */
  methodology_link: string;
  /** Запись расчёта. НЕ показывается пользователю, не переводится. */
  formula: string;
}

export const DRILL_CONTENT: Record<string, DrillMeta> = {
  ctl: {
    key: "ctl",
    methodology_link: "https://www.trainingpeaks.com/learn/articles/the-science-of-the-performance-manager/",
    formula: "CTL(t) = CTL(t−1)·e^(−1/42) + TSS·(1 − e^(−1/42))",
  },
  atl: {
    key: "atl",
    methodology_link: "https://www.trainingpeaks.com/learn/articles/the-science-of-the-performance-manager/",
    formula: "ATL(t) = ATL(t−1)·e^(−1/7) + TSS·(1 − e^(−1/7))",
  },
  tsb: {
    key: "tsb",
    methodology_link: "https://www.trainingpeaks.com/coach-blog/training-stress-balance-explained/",
    formula: "TSB = CTL − ATL",
  },
  monotony: {
    key: "monotony",
    methodology_link: "https://blog.rypt.app/sandc/using-training-monotony-to-design-better-programs/",
    formula: "monotony = mean(7d TSS) / sd(7d TSS)",
  },
  strain: {
    key: "strain",
    methodology_link: "https://blog.rypt.app/sandc/using-training-monotony-to-design-better-programs/",
    formula: "strain = weekly_load × monotony",
  },
  lnrmssd: {
    key: "lnrmssd",
    methodology_link: "https://marcoaltini.substack.com/p/long-term-trends-in-resting-heart",
    formula: "ln(rMSSD) — натуральный логарифм среднеквадратичной разности RR-интервалов",
  },
  cv_hrv: {
    key: "cv_hrv",
    methodology_link: "https://marcoaltini.substack.com/p/heart-rate-variability-coefficient",
    formula: "CV = sd(7d ln(rMSSD)) / mean(7d ln(rMSSD)) × 100%",
  },
  hrv_z_score: {
    key: "hrv_z_score",
    methodology_link: "https://hrv4training.com/blog/heart-rate-variability-normal-values-and-individual-differences",
    formula: "z = (mean(7d) − mean(28d)) / sd(28d)",
  },
  hrv_baseline: {
    key: "hrv_baseline",
    methodology_link: "https://www.frontiersin.org/journals/sports-and-active-living/articles/10.3389/fspor.2025.1714962/full",
    formula: "60-дневное скользящее среднее ln(rMSSD)",
  },
  hrv_recovery: {
    key: "hrv_recovery",
    methodology_link: "https://www.simplifaster.com/articles/measuring-heart-rate-variability-in-athletes/",
    formula: "Скорость возврата HRV к базовому уровню после тяжёлой сессии",
  },
  vo2max: {
    key: "vo2max",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12881131/",
    formula: "Максимальное потребление кислорода (мл/кг/мин), оценочное от часов",
  },
  vo2max_trend: {
    key: "vo2max_trend",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12881131/",
    formula: "Линейная регрессия VO₂max-оценок за 90 дней",
  },
  fatmax: {
    key: "fatmax",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/26794722/",
    formula: "Интенсивность, при которой % окисления жиров максимальна",
  },
  lt1: {
    key: "lt1",
    methodology_link: "https://www.frontiersin.org/articles/10.3389/fphys.2020.585879/full",
    formula: "Темп, при котором лактат начинает расти выше базового",
  },
  lt2: {
    key: "lt2",
    methodology_link: "https://www.frontiersin.org/articles/10.3389/fphys.2020.585879/full",
    formula: "Максимальный лактатный устойчивый темп (MLSS)",
  },
  ftp: {
    key: "ftp",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/33217227/",
    formula: "≈ 95% средней мощности 20-минутного теста (или ramp-тест)",
  },
  css: {
    key: "css",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/40358757/",
    formula: "(400м − 200м) / (t400 − t200)",
  },
  vdot: {
    key: "vdot",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/848370/",
    formula: "Из результата недавней гонки (таблицы Daniels-Gilbert)",
  },
  cp: {
    key: "cp",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10649254/",
    formula: "Асимптота кривой мощность/время (двухпараметрическая модель)",
  },
  w_prime: {
    key: "w_prime",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8854279/",
    formula: "Площадь над CP на кривой мощность/время",
  },
  readiness: {
    key: "readiness",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/30201248/",
    formula: "Композит HRV + сон + субъективный recovery (Polar/Garmin/Oura формула или sRPE-based)",
  },
  sleep_score: {
    key: "sleep_score",
    methodology_link: "https://www.sciencedirect.com/science/article/pii/S1389945724000200",
    formula: "Композитный балл от часов (учитывает TST, SOL, WASO, эффективность)",
  },
  sleep_hours: {
    key: "sleep_hours",
    methodology_link: "https://www.sciencedirect.com/science/article/pii/S1389945724000200",
    formula: "Total Sleep Time (TST)",
  },
  rhr: {
    key: "rhr",
    methodology_link: "https://marcoaltini.substack.com/p/long-term-trends-in-resting-heart",
    formula: "Утренний пульс лёжа сразу после пробуждения",
  },
  rpe: {
    key: "rpe",
    methodology_link: "https://journals.lww.com/acsm-msse/Fulltext/2001/01000/A_New_Approach_to_Monitoring_Exercise_Training.18.aspx",
    formula: "Шкала Foster 1-10 (CR-10)",
  },
  intensity_factor: {
    key: "intensity_factor",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/19204579/",
    formula: "Уровень работы этой тренировки относительно твоего порога",
  },
  rpe_load: {
    key: "rpe_load",
    methodology_link: "https://journals.lww.com/acsm-msse/Fulltext/2001/01000/A_New_Approach_to_Monitoring_Exercise_Training.18.aspx",
    formula: "sRPE × длительность в минутах",
  },
  soreness: {
    key: "soreness",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/26953353/",
    formula: "Шкала 0-10 утром (0 — нет / 10 — максимальная)",
  },
  acwr: {
    key: "acwr",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12487117/",
    formula: "ACWR = mean(7d load) / mean(28d load)",
  },
  weekly_volume: {
    key: "weekly_volume",
    methodology_link: "https://www.uphillathlete.com/training/training-volume/",
    formula: "Сумма часов / километров / TSS за календарную неделю",
  },
  peak_load: {
    key: "peak_load",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12487117/",
    formula: "Максимальный недельный TSS / км за последние N недель",
  },
  ramp_rate: {
    key: "ramp_rate",
    methodology_link: "https://www.trainingpeaks.com/coach-blog/the-three-stage-periodization-plan-for-endurance-athletes/",
    formula: "Δ CTL за последние 7 дней (TSS/неделю)",
  },
  training_age: {
    key: "training_age",
    methodology_link: "https://www.scienceforsport.com/training-age/",
    formula: "Годы последовательной тренировки в дисциплине",
  },
  pacing_cv: {
    key: "pacing_cv",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10890654/",
    formula: "sd(split paces) / mean(split paces) × 100%",
  },
  fade_pct: {
    key: "fade_pct",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/39281580/",
    formula: "(half2_avg_pace − half1_avg_pace) / half1_avg_pace × 100%",
  },
  cardiac_drift: {
    key: "cardiac_drift",
    methodology_link: "https://www.trainingpeaks.com/coach-blog/aerobic-endurance-and-decoupling/",
    formula: "(HR_half2 / pace_half2) / (HR_half1 / pace_half1) × 100%",
  },
  execution_score: {
    key: "execution_score",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11400961/",
    formula: "100 − (pacing_cv×W1 + fade_pct×W2 + drift×W3 + wall_hit×W4)",
  },
  session_distance: {
    key: "session_distance",
    methodology_link: "https://www.uphillathlete.com/training/training-volume/",
    formula: "distance_m / 1000",
  },
  session_duration: {
    key: "session_duration",
    methodology_link: "https://www.uphillathlete.com/training/training-volume/",
    formula: "moving_time (сек) → ч:мм",
  },
  avg_pace: {
    key: "avg_pace",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/848370/",
    formula: "moving_time / distance → сек/км",
  },
  avg_hr: {
    key: "avg_hr",
    methodology_link: "https://www.frontiersin.org/articles/10.3389/fphys.2020.585879/full",
    formula: "среднее heartrate за время движения",
  },
  max_hr: {
    key: "max_hr",
    methodology_link: "https://www.frontiersin.org/articles/10.3389/fphys.2020.585879/full",
    formula: "max(heartrate) за тренировку",
  },
  avg_power: {
    key: "avg_power",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/33217227/",
    formula: "среднее watts за время движения",
  },
  max_power: {
    key: "max_power",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10649254/",
    formula: "max(watts) за тренировку",
  },
  power_balance: {
    key: "power_balance",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/21055708/",
    formula: "доля левой ноги в суммарной мощности, %",
  },
  elevation_gain: {
    key: "elevation_gain",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/12183501/",
    formula: "сумма положительных приращений высоты, м",
  },
  vam: {
    key: "vam",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/12183501/",
    formula: "набор высоты / время подъёма → м/ч",
  },
  kilojoules: {
    key: "kilojoules",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/19229554/",
    formula: "∫ watts dt → кДж",
  },
  effort_km: {
    key: "effort_km",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/12183501/",
    formula: "дистанция_км + набор_м / 100",
  },
  quad_damage: {
    key: "quad_damage",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11129977/",
    formula: "сброс_высоты / 100 · поправка на усилие",
  },
  swolf: {
    key: "swolf",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11678969/",
    formula: "время отрезка (с) + число гребков",
  },
  ground_contact_time: {
    key: "ground_contact_time",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11127892/",
    formula: "среднее время контакта стопы с землёй, мс",
  },
  vertical_oscillation: {
    key: "vertical_oscillation",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11127892/",
    formula: "средняя амплитуда движения корпуса вверх-вниз, см",
  },
  stride_length: {
    key: "stride_length",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11127892/",
    formula: "скорость / частота шагов, м",
  },
  vertical_ratio: {
    key: "vertical_ratio",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11127892/",
    formula: "вертикальные колебания / длина шага, %",
  },
  stance_balance: {
    key: "stance_balance",
    // Тот же обзор беговой биомеханики, что у соседних метрик динамики:
    // баланс времени опоры приходит из того же набора показаний часов.
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11127892/",
    formula: "доля времени опоры на левую ногу, %",
  },
  gap_pace: {
    key: "gap_pace",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/12183501/",
    formula: "темп, приведённый к равнине по стоимости уклона",
  },
  temperature: {
    key: "temperature",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10103870/",
    formula: "средняя температура за тренировку, °C",
  },
  hrr_30s: {
    key: "hrr_30s",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6931845/",
    formula: "падение пульса за 30 с после пика",
  },
  hrr_60s: {
    key: "hrr_60s",
    methodology_link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6931845/",
    formula: "падение пульса за 60 с после пика",
  },
  taper_score: {
    key: "taper_score",
    methodology_link: "https://pubmed.ncbi.nlm.nih.gov/24982200/",
    formula: "f(TSB, sleep_avg, HRV_z, days_to_race)",
  },
};

/**
 * Есть ли карточка для метрики. Текст берётся из словаря `drill`, здесь
 * только «зарегистрирована ли она вообще» — по этому ответу плитка
 * решает, становиться ли ей кнопкой.
 */
export function getDrillContent(metricName: string): DrillMeta | null {
  return DRILL_CONTENT[metricName] ?? null;
}

/** Все ключи с написанным разбором — для тестов покрытия и для сторожа. */
export const DRILL_KEYS: string[] = Object.keys(DRILL_CONTENT);
